import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, authAPI, settingsAPI, teamAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { validateName, validatePhone } from '@/lib/validation';
import {
  Plus, Search, Upload, Filter, MoreHorizontal,
  Phone, Mail, ChevronDown, ChevronLeft, ChevronRight, X, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Utility function to convert to title case
const toTitleCase = (str) => {
  if (!str) return str;
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [agents, setAgents] = useState([]);
  const { user } = useAuth();
  const { settings, stageMap, formatCurrency } = useSettings();
  const navigate = useNavigate();
  const SOURCES = settings.lead_sources || [];
  const stages = settings.pipeline_stages || [];

  const fetchLeads = useCallback(async (searchOverride) => {
    try {
      const params = {
        skip: (page - 1) * limit,
        limit: limit
      };
      const q = searchOverride !== undefined ? searchOverride : search;
      if (q) params.search = q;
      if (stageFilter) params.stage = stageFilter;
      if (sourceFilter) params.source = sourceFilter;
      const res = await leadsAPI.list(params);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, sourceFilter, page, limit]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await teamAPI.members();
      setAgents(res.data || []);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, []);

  // Debounce search input
  const searchTimer = useRef(null);
  const handleSearch = useCallback((value) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchLeads(value), 350);
  }, [fetchLeads]);
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  // Fetch on filter changes (not search — that's debounced)
  const prevFilters = useRef({ stageFilter, sourceFilter });
  useEffect(() => {
    if (prevFilters.current.stageFilter !== stageFilter || prevFilters.current.sourceFilter !== sourceFilter) {
      prevFilters.current = { stageFilter, sourceFilter };
      fetchLeads();
    }
  }, [stageFilter, sourceFilter, fetchLeads]);

  // Initial fetch
  useEffect(() => { fetchLeads(); }, []);

  // Fetch agents for assignment dropdown
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const handleDelete = async (id) => {
    try {
      await leadsAPI.delete(id);
      toast.success('Lead verwijderd');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-5 fade-in" data-testid="leads-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">{total} totaal</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-leads-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm hidden sm:flex">
            <Upload className="w-3.5 h-3.5" /> Importeren
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-lead-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm shadow-sm">
            <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Lead Toevoegen</span><span className="sm:hidden">Nieuw</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
        <div className="relative flex-1 min-w-[150px] sm:min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Zoeken op naam, e-mail, telefoon..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="lead-search-input"
          />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="stage-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Fases</option>
          {stages.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="source-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Bronnen</option>
          {SOURCES.map(s => (
            <option key={s} value={s}>{toTitleCase(s)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <Card className="border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden sm:table-cell">Contact</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden lg:table-cell">Source</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground">Stage</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden md:table-cell">Assigned</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden lg:table-cell">Budget</th>
                <th className="text-right px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground sticky right-0 bg-muted/30">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-b border-border/40 hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/leads/${lead.id}`, { 
                    state: { 
                      from: '/leads',
                      leadIds: leads.map(l => l.id)
                    } 
                  })}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center text-[10px] sm:text-xs font-semibold text-primary flex-shrink-0">
                        {lead.name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{lead.name}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground capitalize truncate sm:hidden">{lead.phone || lead.email}</p>
                        <p className="text-[10px] text-muted-foreground capitalize hidden sm:block">{lead.property_interest}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden sm:table-cell">
                    <div className="space-y-0.5">
                      {lead.email && <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</p>}
                      {lead.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</p>}
                    </div>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden lg:table-cell">
                    <span className="text-xs capitalize">{toTitleCase(lead.source)}</span>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <Badge variant="secondary" className="text-[10px] px-1.5 sm:px-2 py-0.5 text-white" style={{ backgroundColor: stageMap[lead.stage]?.color || '#6B7280' }}>
                      {stageMap[lead.stage]?.label || toTitleCase(lead.stage)}
                    </Badge>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden md:table-cell">
                    <span className="text-xs">{lead.assigned_to_name || '—'}</span>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden lg:table-cell">
                    <span className="text-xs mono">
                      {lead.budget_min || lead.budget_max ? `${formatCurrency(lead.budget_min || 0)} - ${formatCurrency(lead.budget_max || 0)}` : '—'}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-right sticky right-0 bg-background" onClick={(e) => e.stopPropagation()}>
                    <ActionsMenu
                      leadId={lead.id}
                      onView={() => navigate(`/leads/${lead.id}`, { 
                        state: { 
                          from: '/leads',
                          leadIds: leads.map(l => l.id)
                        } 
                      })}
                      onDelete={(user?.role === 'admin' || user?.role === 'manager') ? () => handleDelete(lead.id) : null}
                    />
                  </td>
                </tr>
              ))}
              {leads.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-muted-foreground">
                    No leads found. Add your first lead to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total} leads
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">Page {page} of {Math.ceil(total / limit)}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / limit)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Lead Dialog */}
      <AddLeadDialog open={showAdd} onClose={() => setShowAdd(false)} onSuccess={fetchLeads} agents={agents} />

      {/* Import Dialog */}
      <ImportDialog open={showImport} onClose={() => setShowImport(false)} onSuccess={fetchLeads} />
    </div>
  );
}


function AddLeadDialog({ open, onClose, onSuccess, agents }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', source: 'website',
    budget_min: '', budget_max: '', property_interest: '',
    timeline: '', notes: '', stage: 'new', assigned_to: '',
    custom_fields: {},
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const { settings } = useSettings();
  const stages = settings.pipeline_stages || [];
  const SOURCES = settings.lead_sources || [];

  useEffect(() => {
    if (open) {
      setCustomFieldDefs(settings.custom_fields_leads || []);
      setErrors({});
    }
  }, [open, settings]);

  const validate = () => {
    const e = {};
    const nameErr = validateName(form.name, 'Name', { min: 2, max: 100 });
    if (nameErr) e.name = nameErr;
    if (form.email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email.trim())) e.email = 'Invalid email format';
    if (form.phone) {
      const phoneErr = validatePhone(form.phone);
      if (phoneErr) e.phone = phoneErr;
    }
    if (form.budget_min && isNaN(Number(form.budget_min))) e.budget_min = 'Must be a number';
    if (form.budget_max && isNaN(Number(form.budget_max))) e.budget_max = 'Must be a number';
    if (form.budget_min && form.budget_max && Number(form.budget_max) < Number(form.budget_min)) e.budget_max = 'Must be >= min budget';
    if (form.notes && form.notes.length > 2000) e.notes = 'Max 2000 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await leadsAPI.create({
        ...form,
        name: form.name.trim(),
        email: form.email?.trim().toLowerCase() || null,
        phone: form.phone ? form.phone.replace(/[\s\-\(\)]/g, '') : null,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
      });
      toast.success('Lead aangemaakt');
      onSuccess();
      onClose();
      setForm({ name: '', email: '', phone: '', source: 'website', budget_min: '', budget_max: '', property_interest: '', timeline: '', notes: '', stage: 'new', assigned_to: '', custom_fields: {} });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create lead');
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field) => errors[field] ? <p className="text-[10px] text-destructive mt-0.5">{errors[field]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} className={errors.name ? 'border-destructive' : ''} data-testid="lead-name-input" />
              {fieldError('name')}
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} className={errors.email ? 'border-destructive' : ''} data-testid="lead-email-input" />
              {fieldError('email')}
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value}))} placeholder="+14155551234" className={errors.phone ? 'border-destructive' : ''} data-testid="lead-phone-input" />
              {fieldError('phone')}
            </div>
            <div>
              <Label>Source</Label>
              <select value={form.source} onChange={(e) => setForm(p => ({...p, source: e.target.value}))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
{SOURCES.map(s => <option key={s} value={s}>{toTitleCase(s)}</option>)}
</select>
            </div>
            <div>
              <Label>Stage</Label>
              <select value={form.stage} onChange={(e) => setForm(p => ({...p, stage: e.target.value}))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
{stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
</select>
            </div>
            <div>
              <Label>Budget Min</Label>
              <Input type="number" min="0" value={form.budget_min} onChange={(e) => setForm(p => ({...p, budget_min: e.target.value}))} className={errors.budget_min ? 'border-destructive' : ''} />
              {fieldError('budget_min')}
            </div>
            <div>
              <Label>Budget Max</Label>
              <Input type="number" min="0" value={form.budget_max} onChange={(e) => setForm(p => ({...p, budget_max: e.target.value}))} className={errors.budget_max ? 'border-destructive' : ''} />
              {fieldError('budget_max')}
            </div>
            <div>
              <Label>Interest</Label>
              <select value={form.property_interest} onChange={(e) => setForm(p => ({...p, property_interest: e.target.value}))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
{(settings.property_interests || ['residential', 'commercial', 'luxury', 'investment']).map(t => <option key={t} value={t}>{toTitleCase(t)}</option>)}
</select>
            </div>
            <div>
              <Label>Timeline</Label>
              <select value={form.timeline} onChange={(e) => setForm(p => ({...p, timeline: e.target.value}))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
{(settings.lead_timelines || ['immediate', '1-3 months', '3-6 months', '6-12 months']).map(t => <option key={t} value={t}>{toTitleCase(t)}</option>)}
</select>
            </div>
            {agents.length > 0 && (
              <div className="col-span-2">
                <Label>Assign to</Label>
                <select value={form.assigned_to} onChange={(e) => setForm(p => ({...p, assigned_to: e.target.value}))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
<option value="">Unassigned</option>
{agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name} ({a.role})</option>)}
</select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({...p, notes: e.target.value}))} rows={2} />
            </div>
            {customFieldDefs.length > 0 && (
              <>
                <div className="col-span-2 border-t pt-3 mt-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Custom Fields</p>
                </div>
                {customFieldDefs.map((cf) => (
                  <div key={cf.key} className={cf.type === 'textarea' ? 'col-span-2' : ''}>
                    <Label className="text-xs">{cf.label} {cf.required && <span className="text-destructive">*</span>}</Label>
                    <CustomFieldInput field={cf} value={form.custom_fields?.[cf.key] || ''} onChange={(val) => setForm(p => ({...p, custom_fields: {...(p.custom_fields || {}), [cf.key]: val}}))} />
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-lead-btn">{saving ? 'Opslaan...' : 'Lead Toevoegen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ImportDialog({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await leadsAPI.importCSV(formData);
      toast.success(`Imported ${res.data.imported} leads`);
      onSuccess();
      onClose();
      setFile(null);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Import failed';
      
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map(e => e.msg || e).join(', ');
        } else if (errorDetail.message) {
          errorMessage = errorDetail.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import Leads from CSV</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Upload a CSV or Excel file with columns: name, email, phone, source, budget_min, budget_max, property_interest, timeline, notes</p>
          <Input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="csv-file-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleImport} disabled={!file || importing} data-testid="import-submit-btn">
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CustomFieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
{(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
</select>
      );
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'checkbox':
      return (
        <div className="flex items-center gap-2 pt-1">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="rounded border-input" />
          <span className="text-xs text-muted-foreground">{field.placeholder || 'Yes'}</span>
        </div>
      );
    case 'number':
      return <Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" required={field.required} />;
    default:
      return <Input type={field.type || 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
  }
}


function ActionsMenu({ leadId, onView, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 140 });
    }
    setOpen(!open);
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        data-testid={`lead-actions-${leadId}`}
        onClick={handleOpen}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 min-w-[140px] bg-background border border-border rounded-md shadow-lg py-1"
            style={{ top: pos.top, left: pos.left }}
          >
            <button
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              onClick={() => { setOpen(false); onView(); }}
              data-testid={`action-view-${leadId}`}
            >
              View Details
            </button>
            {onDelete && (
              <button
                className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-muted transition-colors"
                onClick={() => { setOpen(false); onDelete(); }}
                data-testid={`action-delete-${leadId}`}
              >
                Delete
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
